import { BaseEntitiesActions, EntitiesResult } from './baseModelStore';
import { AppThunk } from '../store';
import { AxiosResponse } from 'axios';
import { ModelReducers } from '../modelReducers';
import { Action } from '@reduxjs/toolkit';
import { pick } from 'lodash';

export type AdditionalAction = Action<string> | any;

export const dispatchAdditionalActions: <T>(
  dispatch: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actions: ((parameters: any) => AdditionalAction)[],
  data: T
) => void = (dispatch, actions, data): void => {
  actions.forEach((actionCreator) => {
    const action = actionCreator(data);

    if ((action as Action<string>).type) {
      dispatch(action as Action<string>);
    } else {
      dispatch(action as any);
    }
  });
};

export const fetchEntities = <T>(
  name: ModelReducers,
  actions: BaseEntitiesActions,
  getAction: (ids: string[]) => Promise<AxiosResponse<EntitiesResult<T>>>
) => (ids: string[], additionalActions?: ((response: EntitiesResult<T>) => AdditionalAction)[]): AppThunk => {
  return async (dispatch, getState) => {
    const { entities } = getState()[name];
    const idsToFetch = ids.filter((id) => !entities[id]);
    if (!idsToFetch.length) {
      if (additionalActions)
        dispatchAdditionalActions(
          dispatch,
          additionalActions,
          ids.map((id) => entities[id])
        );
      return;
    }
    try {
      dispatch(actions.startLoading());
      const entities = (await getAction(idsToFetch)).data;
      dispatch(actions.getEntitiesSuccess(entities));
      if (additionalActions) dispatchAdditionalActions(dispatch, additionalActions, entities);
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };
};

export const fetchAllEntities = <T>(
  name: ModelReducers,
  actions: BaseEntitiesActions,
  getAction: (additionalParams?: string) => Promise<AxiosResponse<EntitiesResult<T>>>
) => (
  additionalParams?: string,
  additionalActions?: ((response: EntitiesResult<T>) => AdditionalAction)[]
): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(actions.startLoading());
      const entities = (await getAction(additionalParams)).data;
      dispatch(actions.getEntitiesSuccess(entities));
      if (additionalActions) dispatchAdditionalActions(dispatch, additionalActions, entities);
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };
};

export const fetchAllEntitiesMap = <T>(
  name: ModelReducers,
  actions: BaseEntitiesActions,
  getAction: () => Promise<AxiosResponse<{ [id: string]: T }>>
) => (additionalActions?: ((response: { [id: string]: T }) => AdditionalAction)[]): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(actions.startLoading());
      const entities = (await getAction()).data;
      dispatch(actions.getEntitiesMapSuccess(entities));
      if (additionalActions) dispatchAdditionalActions(dispatch, additionalActions, entities);
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };
};

export const fetchEntity = <T>(
  name: ModelReducers,
  actions: BaseEntitiesActions,
  getAction: (id: string) => Promise<AxiosResponse<T>>
) => {
  return (id: string, additionalActions?: ((response: T) => AdditionalAction)[]): AppThunk => async (
    dispatch,
    getState
  ) => {
    const { entities } = getState()[name];
    if (entities[id]) {
      if (additionalActions) dispatchAdditionalActions(dispatch, additionalActions, entities[id]);
      return;
    }
    try {
      dispatch(actions.startLoading());
      const entity = (await getAction(id)).data;
      dispatch(actions.getEntitySuccess(entity));
      if (additionalActions) dispatchAdditionalActions(dispatch, additionalActions, entity);
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };
};

export const fetchEntitiesMap = <T>(
  name: ModelReducers,
  actions: BaseEntitiesActions,
  getAction: (ids: string[]) => Promise<AxiosResponse<{ [id: string]: T }>>
) => {
  return (
    ids: string[],
    additionalActions?: ((response: { [id: string]: T }) => AdditionalAction)[]
  ): AppThunk => async (dispatch, getState) => {
    const { entities } = getState()[name];
    const idsToFetch = ids.filter((id) => !entities[id]);
    if (!idsToFetch.length) {
      if (additionalActions) dispatchAdditionalActions(dispatch, additionalActions, pick(entities, ids));
      return;
    }
    try {
      dispatch(actions.startLoading());
      const entities = (await getAction(idsToFetch)).data;
      dispatch(actions.getEntitiesMapSuccess(entities));
      if (additionalActions) dispatchAdditionalActions(dispatch, additionalActions, entities);
    } catch (err) {
      dispatch(actions.loadingFailed(err.toString()));
    }
  };
};
